import {
  SEARCH_PANEL_GROUP_ROWS,
  type SearchHit,
  type SearchResponse,
} from '@palaccount/contracts';
import type { TFunction } from 'i18next';
import { Box, FileText, Landmark, Search, SearchX, User, Users } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { formatMoney, trimTrailingZeros } from '@/lib/format';
import { cn } from '@/lib/utils';

import { isSearchable, useGlobalSearch } from './api';
import { groupLabelKey, iconFor, usePageMatches, type NavPage } from './nav-index';
import {
  DOCUMENT_LABEL_KEYS,
  GROUP_LABEL_KEYS,
  flatten,
  presentGroups,
  routeFor,
  totalAcross,
} from './result-routes';

/**
 * GEN-12 — THE TOP BAR'S SEARCH, box and panel together.
 *
 * §4.3.2 is a specification rather than a transcription: *"`GEN-12` requires
 * the search to work. This is the surface that shows what it finds. The
 * prototype draws the box and wires nothing behind it, so every line below is
 * a specification."* The box existed for the whole build and did nothing.
 *
 * The box and the panel are ONE component because they are one control: a
 * combobox keeps focus in its input and moves a highlight in the popup
 * through `aria-activedescendant`, so the arrow keys arriving at the input
 * have to reach the panel's cursor. Splitting them meant hoisting that cursor
 * somewhere both could see, and the first draft of this file did it with a
 * module-level ref — which is a single shared cursor for what is nominally a
 * reusable component, and would have broken the moment a second one existed.
 *
 * ── THE FOUR STATES (§4.3.2's own table) ───────────────────────────────────
 *
 *   Fewer than 2 characters  Nothing. The panel stays closed.
 *   Searching                Three grey shimmer rows, after 300 ms (GEN-159)
 *   Results                  The grouped rows
 *   Nothing found            A `search_off` icon and "Nothing matches `x`."
 *                            with NO BUTTON
 *
 * ── PAGES: A SIXTH GROUP, FIRST, AND ANSWERED IN THE BROWSER ───────────────
 *
 * Bugs G-05 / UB-02: the box must also find SCREENS — "فاتورة مشتريات" opens
 * Purchase Invoices, "new sales invoice" opens the empty editor. The
 * navigation index (`nav-index.ts`) answers that from the dictionary already
 * in the page, in both languages at once, so it is drawn FIRST and at once:
 * it never waits for the debounce, and the server's four states below apply
 * only to the record groups under it. "Nothing found" is therefore the state
 * where NEITHER the pages nor the server matched.
 *
 * ── WHAT IS LOAD-BEARING ───────────────────────────────────────────────────
 *
 * **GEN-12c — "fully operable from the keyboard, with a visible highlight."**
 * Down and up move "through the rows, ACROSS GROUP BOUNDARIES", so the cursor
 * is an index into one FLATTENED list and the grouping is drawn over it. A
 * per-group cursor would need a rule for the bottom of a group; §4.3.2 has
 * one already — you carry on into the next. The page rows are the head of
 * that same list.
 *
 * **GEN-12d — every row carries its code**, in monospace at the trailing edge
 * AND inside the row's accessible name. Two items called "Twin Widget" must
 * be two distinguishable rows for a screen-reader user too, which is the
 * masters lesson: several controls sharing one accessible name makes every
 * query for them ambiguous, and here it would defeat the rule outright.
 *
 * **RTL is a layout, not a translation.** Logical properties throughout, and
 * every mixed-script fragment wrapped in `<bdi>`. An Arabic partner name
 * beside a Latin code is exactly what the bidi algorithm reorders wrongly,
 * and `BP-2260` rendering as `2260-BP` is not cosmetic — it is a different
 * number.
 */

/** One keyboard-walkable row: a page from the index, or a record from the server. */
export type SearchRow = { kind: 'page'; page: NavPage } | { kind: 'hit'; hit: SearchHit };

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reactId = React.useId();
  const inputId = `${reactId}-search`;
  const listId = `${reactId}-results`;

  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { data, isFetching } = useGlobalSearch(query);
  const pages = usePageMatches(isSearchable(query) ? query : '');
  // The panel truncates pages exactly as it truncates every other group.
  const pageRows = pages.slice(0, SEARCH_PANEL_GROUP_ROWS);

  const groups = presentGroups(data);
  const rows: SearchRow[] = [
    ...pageRows.map((page): SearchRow => ({ kind: 'page', page })),
    ...flatten(data).map((hit): SearchRow => ({ kind: 'hit', hit })),
  ];
  const total = totalAcross(data) + pages.length;
  /*
   * §4.3.2: "**See all N results** … shown only when more than 5 results
   * exist in ANY group". Read literally — the PER-GROUP total decides,
   * because the panel truncates per group and the button exists to reach
   * what that truncation hid.
   */
  const truncated =
    pages.length > SEARCH_PANEL_GROUP_ROWS ||
    groups.some((group) => group.total > SEARCH_PANEL_GROUP_ROWS);

  // GEN-12b's first half: "The panel must open at the second typed character".
  // The panel opens on the CHARACTER; only the request waits out the 250 ms
  // (see `useDebounced`), so the panel never flickers open a beat late.
  const showPanel = open && isSearchable(query);

  React.useEffect(() => {
    // A new result set invalidates the old cursor: keeping index 7 after the
    // list shrank to two rows is how Enter opens something nobody looked at.
    // The page rows change on the keystroke itself, hence `query` too.
    setActive(0);
  }, [data, query]);

  /** GEN-159 — the shimmer waits 300 ms, so a fast answer never flashes one. */
  const [showShimmer, setShowShimmer] = React.useState(false);
  React.useEffect(() => {
    if (!isFetching) {
      setShowShimmer(false);
      return;
    }
    const timer = setTimeout(() => setShowShimmer(true), 300);
    return () => clearTimeout(timer);
  }, [isFetching]);

  const openRow = (row: SearchRow) => {
    void navigate(row.kind === 'page' ? row.page.to : routeFor(row.hit));
    setOpen(false);
  };

  const seeAll = () => {
    void navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    setOpen(false);
  };

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!showPanel || rows.length === 0) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + rows.length) % rows.length);
      return;
    }
    if (event.key === 'Enter') {
      if (!showPanel) return;
      const row = rows[active];
      // With no rows there is nothing to open, and swallowing Enter would
      // make the box feel broken rather than empty.
      if (row === undefined) return;
      event.preventDefault();
      openRow(row);
      return;
    }
    if (event.key === 'Escape') {
      if (!showPanel) return;
      event.preventDefault();
      // §4.3.2: "Closes the panel and LEAVES THE TYPED TEXT IN THE BOX" —
      // which is why this does not touch `query`.
      setOpen(false);
    }
  }

  return (
    <div
      className="relative w-full"
      onBlur={(event) => {
        // §4.3.2: "Clicking outside … Closes the panel". Unless the focus went
        // INSIDE the panel, or clicking a row would close the list before the
        // click landed on it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Search
        className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-label={t('shell.searchLabel')}
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          showPanel && rows.length > 0 ? `${listId}-${active}` : undefined
        }
        autoComplete="off"
        placeholder={t('shell.searchPh')}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="h-10 w-full rounded-md border border-transparent bg-muted pe-3 ps-9 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
      />

      {showPanel ? (
        <ul
          id={listId}
          role="listbox"
          /*
           * NO `aria-label` on the popup. It is named by the combobox that
           * owns it (`aria-controls` points here, `aria-activedescendant` at
           * the row inside it), and a second copy of the name is what made
           * `getByLabel('Search')` ambiguous the moment the panel opened —
           * the typeahead learned this across fifteen failing journeys.
           *
           * §4.3.2: "drops below the search box, is as wide as the box, and
           * never taller than 420 pixels before it scrolls INSIDE ITSELF", so
           * the page behind it never moves. `start-0`/`end-0` rather than
           * left/right, so it lands under the box in Arabic too.
           */
          className="absolute start-0 end-0 z-50 mt-1 max-h-[420px] overflow-y-auto rounded-md border border-border bg-popover py-1 text-sm shadow-lg"
        >
          <PanelBody
            data={data}
            isFetching={isFetching}
            showShimmer={showShimmer}
            query={query}
            pages={pageRows}
            pagesTotal={pages.length}
            groups={groups}
            listId={listId}
            active={active}
            onHover={setActive}
            onPick={openRow}
          />
          {rows.length > 0 && truncated ? (
            <li role="presentation" className="border-t border-border p-1">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={seeAll}
                className="w-full rounded-md px-3 py-2 text-center text-sm font-medium text-primary hover:bg-accent"
              >
                {t('search.seeAll', { count: total })}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/** The uppercase group heading with its FULL total beside it (§4.3.2). */
function GroupHeading({ label, total }: { label: string; total: number }) {
  return (
    <li
      role="presentation"
      className="flex items-center justify-between px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
    >
      <span>{label}</span>
      {/* The FULL total, not the rows drawn — §4.3.2 puts it beside the
          heading and derives "See all N results" from it. */}
      <span className="tabular-nums">{total}</span>
    </li>
  );
}

function PanelBody({
  data,
  isFetching,
  showShimmer,
  query,
  pages,
  pagesTotal,
  groups,
  listId,
  active,
  onHover,
  onPick,
}: {
  data: SearchResponse | undefined;
  isFetching: boolean;
  showShimmer: boolean;
  query: string;
  pages: readonly NavPage[];
  pagesTotal: number;
  groups: { key: keyof SearchResponse; total: number; rows: SearchHit[] }[];
  listId: string;
  active: number;
  onHover: (index: number) => void;
  onPick: (row: SearchRow) => void;
}) {
  const { t } = useTranslation();
  const hits = groups.flatMap((group) => group.rows);

  // The pages group is drawn first, out of the dictionary, with no request
  // behind it — so it is never in the searching state and never shimmers.
  let index = -1;
  const pagesBlock =
    pages.length === 0 ? null : (
      <>
        <GroupHeading label={t('search.groupPages')} total={pagesTotal} />
        {pages.map((page) => {
          index += 1;
          const at = index;
          return (
            <PageRow
              key={page.to}
              page={page}
              id={`${listId}-${at}`}
              active={at === active}
              onHover={() => onHover(at)}
              onPick={() => onPick({ kind: 'page', page })}
            />
          );
        })}
      </>
    );

  // STATE 2 — searching. Only while there is nothing to show: with a previous
  // answer on screen (`keepPreviousData`) replacing it with shimmer would
  // take away rows the reader is mid-way through reading.
  if (isFetching && data === undefined) {
    if (!showShimmer) return pagesBlock;
    return (
      <>
        {pagesBlock}
        <li role="presentation" className="p-2" aria-label={t('search.searching')}>
          {[0, 1, 2].map((row) => (
            <div key={row} className="mb-2 h-9 animate-pulse rounded-md bg-muted last:mb-0" />
          ))}
        </li>
      </>
    );
  }

  // STATE 4 — nothing found: neither a page nor a record.
  if (data !== undefined && hits.length === 0) {
    if (pagesBlock !== null) return pagesBlock;
    return (
      <li
        role="presentation"
        className="flex flex-col items-center gap-2 px-3 py-8 text-center text-sm text-muted-foreground"
      >
        {/* §4.3.2's `search_off` icon, and NO BUTTON — the section says so in
            as many words, and a "create one?" button here would offer a
            different screen's job from a panel that cannot do it. */}
        <SearchX className="size-6" aria-hidden />
        <span>
          {t('search.nothingMatches')} <bdi className="font-medium">{query.trim()}</bdi>
        </span>
      </li>
    );
  }

  // STATE 3 — results.
  return (
    <>
      {pagesBlock}
      {groups.map((group) => (
        <React.Fragment key={group.key}>
          <GroupHeading label={t(GROUP_LABEL_KEYS[group.key])} total={group.total} />
          {group.rows.map((hit) => {
            index += 1;
            const at = index;
            return (
              <ResultRow
                key={`${hit.type}-${hit.id}`}
                hit={hit}
                id={`${listId}-${at}`}
                active={at === active}
                onHover={() => onHover(at)}
                onPick={() => onPick({ kind: 'hit', hit })}
              />
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * A PAGE row (G-05): the group's icon, the page's name in bold, the group's
 * name as the grey second line. No code — a screen has none — so the group
 * name joins the accessible name instead, which is what tells "Sales" the
 * dashboard tab from "Sales" the menu group for a screen-reader user.
 */
export function PageRow({
  page,
  id,
  active,
  onHover,
  onPick,
}: {
  page: NavPage;
  id: string;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const Icon = iconFor(page);
  const primary = t(page.labelKey);
  const secondary = t(groupLabelKey(page));

  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      aria-label={`${primary} · ${secondary}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPick}
      onMouseEnter={onHover}
      className={cn(
        'flex cursor-pointer items-start gap-3 px-3 py-2',
        active ? 'bg-accent text-accent-foreground' : undefined,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <bdi className="block truncate font-semibold">{primary}</bdi>
        <bdi className="block truncate text-xs text-muted-foreground">{secondary}</bdi>
      </div>
    </li>
  );
}

/**
 * §4.3.2: "a type icon, a bold primary line, the record's code in monospace at
 * the trailing edge, and one grey secondary line".
 */
export function ResultRow({
  hit,
  id,
  active,
  onHover,
  onPick,
}: {
  hit: SearchHit;
  id: string;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const Icon = ICONS[hit.type];
  const primary = primaryLine(hit, t);
  const secondary = secondaryLine(hit, t);

  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      /*
       * The DISTINCT accessible name (GEN-12d): the code is in it, so two
       * records sharing a name are two different names here. Without it a
       * screen-reader user hears "Twin Widget" twice and the rule's whole
       * purpose — telling them apart — is lost in the one place it matters
       * most.
       */
      aria-label={`${primary} ${hit.code}`}
      // The row takes the click, never the focus: preventDefault on mousedown
      // keeps the caret in the box so the panel does not close underneath the
      // pointer before the click lands.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPick}
      onMouseEnter={onHover}
      className={cn(
        'flex cursor-pointer items-start gap-3 px-3 py-2',
        active ? 'bg-accent text-accent-foreground' : undefined,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          {/* <bdi> on both halves: an Arabic name beside a Latin code is the
              exact case the bidi algorithm reorders wrongly. */}
          <bdi className="truncate font-semibold">{primary}</bdi>
          <bdi className="shrink-0 font-mono text-xs text-muted-foreground">{hit.code}</bdi>
        </div>
        {secondary === null ? null : (
          <bdi className="block truncate text-xs text-muted-foreground">{secondary}</bdi>
        )}
      </div>
    </li>
  );
}

const ICONS: Record<SearchHit['type'], typeof Box> = {
  item: Box,
  partner: Users,
  document: FileText,
  account: Landmark,
  employee: User,
};

/**
 * i18next's own function type rather than a hand-rolled one. The first draft
 * declared `(key: string, opts?: …) => string` and it did not fit: under
 * `exactOptionalPropertyTypes` an optional parameter is not the same type as
 * one that may be `undefined`, so a `TFunction` was not assignable to it. The
 * library's type is the one the library satisfies.
 */
type Translate = TFunction<'translation', undefined>;

/** The bold line, per §4.3.2's table. */
export function primaryLine(hit: SearchHit, t: Translate): string {
  // "Documents | The document type and number" — the type's name, translated;
  // the number is the code already drawn at the row's trailing edge.
  if (hit.type === 'document') return t(DOCUMENT_LABEL_KEYS[hit.kind]);
  return hit.title;
}

/**
 * The grey line, per §4.3.2's table. Composed HERE, in the browser, so it is
 * in the READER's language — which is why the server sends fields rather than
 * a sentence.
 */
export function secondaryLine(hit: SearchHit, t: Translate): string | null {
  switch (hit.type) {
    /** "Items | Category · price · on-hand quantity" */
    case 'item': {
      /*
       * `trimTrailingZeros` rather than `formatQty`, and the difference is a
       * decision rather than a shortcut. N-4 prints a quantity "with the
       * number of decimals the item's UNIT allows, and always with the unit
       * written next to them" — which needs the item's stock unit, a join per
       * row, to render a hint. §4.3.2's own example is "128 on hand", a bare
       * count with no unit, so the panel prints the stored figure with its
       * trailing zeros trimmed: exact, never rounded, and never claiming a
       * precision it did not look up. A search row is a way to FIND the item;
       * the item's own screen is where its stock is read.
       */
      const parts = [
        hit.category,
        formatMoney(hit.price),
        t('search.onHand', { qty: trimTrailingZeros(hit.onHand) }),
      ];
      return parts.filter((part): part is string => part !== null && part.length > 0).join(' · ');
    }
    /** "Business partners | Their types · what they owe or are owed" */
    case 'partner': {
      const types = [
        hit.isCustomer ? t('search.typeCustomer') : null,
        hit.isSupplier ? t('search.typeSupplier') : null,
      ].filter((part): part is string => part !== null);
      /*
       * The sign is a DIRECTION, and the two directions are different
       * sentences rather than one sentence with a minus in it: §4.3.2's own
       * example reads "Supplier · owes 4,200.00", and "owes −4,200.00" would
       * make a reader work out which way round it is.
       */
      const owed = hit.owed.startsWith('-')
        ? t('search.weOwe', { amount: formatMoney(hit.owed.slice(1)) })
        : t('search.owes', { amount: formatMoney(hit.owed) });
      return [types.join(' / '), owed].filter((part) => part.length > 0).join(' · ');
    }
    /** "Documents | The partner · the document date" */
    case 'document':
      return [hit.partner, hit.date].filter((part): part is string => part !== null).join(' · ');
    /** "Accounts | Its code and its balance" */
    case 'account':
      return `${hit.code} · ${formatMoney(hit.balance)}`;
    /** "Employees | Their job title and department" */
    case 'employee':
      return `${hit.jobTitle} · ${hit.department}`;
  }
}
